import { getLinkForCourseLesson } from "../../../../services/Utils";
import lesson0108 from "../lesson-08/lesson";
import lesson0104 from "../lesson-04/lesson";
import lesson0125 from "../lesson-25/lesson";
import lesson0103 from "../lesson-03/lesson";
import lesson0122 from "../lesson-22/lesson";
import lesson0102 from "../lesson-02/lesson";
import course03 from "../../../course/course-03/course";
import lesson0304 from "../../course-03/lesson-04/lesson";

const linkLesson0108 = getLinkForCourseLesson("41545ab2-1f99-40ea-8225-21d5e62ab7b0", lesson0108.id);
const linkLesson0104 = getLinkForCourseLesson("41545ab2-1f99-40ea-8225-21d5e62ab7b0", lesson0104.id);
const linkLesson0125 = getLinkForCourseLesson("41545ab2-1f99-40ea-8225-21d5e62ab7b0", lesson0125.id);
const linkLesson0103 = getLinkForCourseLesson("41545ab2-1f99-40ea-8225-21d5e62ab7b0", lesson0103.id);
const linkLesson0122 = getLinkForCourseLesson("41545ab2-1f99-40ea-8225-21d5e62ab7b0", lesson0122.id);
const linkLesson0102 = getLinkForCourseLesson("41545ab2-1f99-40ea-8225-21d5e62ab7b0", lesson0102.id);
const linkLesson0304 = getLinkForCourseLesson(course03.id, lesson0304.id);

const theory = `<p>Acoustics is the science of sound. Originally a sound is a phenomenon perceived by the ear, the organ of hearing. This definition restricts sound to waves propagating in air, or even in water, for a frequency range from 20 Hz to 20 kHz. Below 20 Hz sound waves are infrasounds and beyond 20 kHz ultrasounds. This distinction pertaining to physiology is not relevant in physics: infrasound and ultrasound are considered as two classes of sound. In the same way any wave propagating in a fluid is considered as a sound. Indeed it is possible to hear quite well in water. By extension, waves in solids are also considered to be sounds. We speak for example of Non-Destructive Testing (NDT) by ultrasound. However, for simplicity, the present course is only devoted to sounds in fluids as waves in solids involve different kinds of waves not present in fluids (shear waves, Lamb waves, torsional waves, bending waves etc.).
<h3 class=sectionHead>Sound as a signal</h3>
<p>When a sound is detected by the ears it can be considered as a signal. Signals can be divided into two classes : signal with finite duration and signals with infinite duration. This distinction may be considered somewhat odd, since no signal has an infinite duration. However, any signal with an indefinite duration, such as a beep or a permanent and stable noise, whose statistical properties are constant during a given lapse of time, is considered to be infinite. Among these class we may consider periodic signals and stationary noise. For these signals two important indicators of global properties can be defined: the mean value and the Root Mean Square (RMS) value.
<h4 class=subsectionHead>Mean and RMS values</h4>
<p>
   The mean value of a signal
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>s</mi>
      <mo class=MathClass-open>(</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   can be defined as
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mover accent=true>
               <mrow>
                  <mi>s</mi>
               </mrow>
               <mo accent=true>¯</mo>
            </mover>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <mi>T</mi>
               </mrow>
            </mfrac>
            <msubsup>
               <mrow>
                  <mo>∫</mo>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
               <mrow>
                  <mi>T</mi>
               </mrow>
            </msubsup>
            <mi>s</mi>
            <mo class=MathClass-open>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close>)</mo>
            <mstyle class=text>
               <mtext>d</mtext>
            </mstyle>
            <mi>t</mi>
         </math>
      <td class=eq-no>(1)
</table>
<p>
   where
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>T</mi>
   </math>
   is in practice an arbitrary time. So,
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mover accent=true>
         <mrow>
            <mi>s</mi>
         </mrow>
         <mo accent=true>¯</mo>
      </mover>
   </math>
   depends on the integration time but in practice is taken large enough so that it is close to the asymptotic value when
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>T</mi>
   </math>
   tends to infinite. When the signal is periodic
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>T</mi>
   </math>
  can be taken as the exact period. The RMS value comes from the concept of power in electricity but also used in acoustics (see <a href="${linkLesson0108}" target="_blank"> Lesson 8</a>). It is related to the signal squared. So the RMS value is defined as
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <msub>
               <mrow>
                  <mi>s</mi>
               </mrow>
               <mrow>
                  <mi>R</mi>
                  <mi>M</mi>
                  <mi>S</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <mi>T</mi>
               </mrow>
            </mfrac>
            <msubsup>
               <mrow>
                  <mo>∫</mo>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
               <mrow>
                  <mi>T</mi>
               </mrow>
            </msubsup>
            <mi>s</mi>
            <msup>
               <mrow>
                  <mo class=MathClass-open>(</mo>
                  <mi>t</mi>
                  <mo class=MathClass-close>)</mo>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msup>
            <mstyle class=text>
               <mtext>d</mtext>
            </mstyle>
            <mi>t</mi>
         </math>
      <td class=eq-no>(2)
</table>
<p>
   As well as for the mean value the integration boundaries will be limited to a time
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>T</mi>
   </math>
   which can be the period for a periodic signal. Since acoustics deals with fluctuations the mean value is often removed and the ACRMS value or standard deviation is considered
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <msub>
               <mrow>
                  <mi>s</mi>
               </mrow>
               <mrow>
                  <mi>A</mi>
                  <mi>C</mi>
                  <mi>R</mi>
                  <mi>M</mi>
                  <mi>S</mi>
               </mrow>
            </msub>
            <mo class=MathClass-rel>=</mo>
            <mfrac>
               <mrow>
                  <mn>1</mn>
               </mrow>
               <mrow>
                  <mi>T</mi>
               </mrow>
            </mfrac>
            <msubsup>
               <mrow>
                  <mo>∫</mo>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
               <mrow>
                  <mi>T</mi>
               </mrow>
            </msubsup>
            <msup>
               <mrow>
                  <mo class=MathClass-open>(</mo>
                  <mi>s</mi>
                  <mo class=MathClass-open>(</mo>
                  <mi>t</mi>
                  <mo class=MathClass-close>)</mo>
                  <mo class=MathClass-bin>−</mo>
                  <mover accent=true>
                     <mrow>
                        <mi>s</mi>
                     </mrow>
                     <mo accent=true>¯</mo>
                  </mover>
                  <mo class=MathClass-close>)</mo>
               </mrow>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msup>
            <mstyle class=text>
               <mtext>d</mtext>
            </mstyle>
            <mi>t</mi>
         </math>
      <td class=eq-no>(3)
</table>
<p>
   The following formula can be useful:
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msubsup>
         <mrow>
            <mi>s</mi>
         </mrow>
         <mrow>
            <mi>R</mi>
            <mi>M</mi>
            <mi>S</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msubsup>
      <mo class=MathClass-rel>=</mo>
      <msubsup>
         <mrow>
            <mi>s</mi>
         </mrow>
         <mrow>
            <mi>A</mi>
            <mi>C</mi>
            <mi>R</mi>
            <mi>M</mi>
            <mi>S</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msubsup>
      <mo class=MathClass-bin>+</mo>
      <msup>
         <mrow>
            <mover accent=true>
               <mrow>
                  <mi>s</mi>
               </mrow>
               <mo accent=true>¯</mo>
            </mover>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msup>
   </math>
   . Among the periodic signals, one type of signal is of particular importance: the sinusoidal signal
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>s</mi>
      <mo class=MathClass-open>(</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <mi>A</mi>
      <mi class=qopname>cos</mi>
      <mo>⁡</mo>
      <mo class=MathClass-open>(</mo>
      <mn>2</mn>
      <mi>π</mi>
      <mi>t</mi>
      <mo class=MathClass-bin>∕</mo>
      <mi>T</mi>
      <mo class=MathClass-bin>+</mo>
      <mi>ϕ</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   where
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>T</mi>
   </math>
   is the period,
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>A</mi>
   </math>
   the amplitude and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>ϕ</mi>
   </math>
   the phase. This signal is sometimes designed as pure tone or monochromatic signal by analogy with optics for which monochromatic light corresponds to a sinusoidal signal of given frequency. Its mean value is zero and its RMS amplitude value is
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>s</mi>
         </mrow>
         <mrow>
            <mi>R</mi>
            <mi>M</mi>
            <mi>S</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <mi>A</mi>
         </mrow>
         <mrow>
            <msqrt>
               <mrow>
                  <mn>2</mn>
               </mrow>
            </msqrt>
         </mrow>
      </mfrac>
   </math>
   .
<h4 class=subsectionHead>Finite duration signal</h4>
<p>
   For a signal with a finite duration, neither RMS value nor mean value are relevant because the result might depend on the value of
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>T</mi>
   </math>
   . In that case the total ”signal energy” has to be considered:
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>E</mi>
      <mo class=MathClass-rel>=</mo>
      <msubsup>
         <mrow>
            <mi class=MathClass-op>∫</mi>
            <mo>⁡</mo>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
         <mrow>
            <mi>T</mi>
         </mrow>
      </msubsup>
      <mi>s</mi>
      <msup>
         <mrow>
            <mo class=MathClass-open>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close>)</mo>
         </mrow>
         <mrow>
            <mn>2</mn>
         </mrow>
      </msup>
      <mstyle class=text>
         <mtext>d</mtext>
      </mstyle>
      <mi>t</mi>
   </math>
   where time interval [0 T] contains all the signal. Note that the ”signal energy” does not have the dimensions of energy. For example, if the signal is a voltage, the ”signal energy” must be divided by an electrical impedance to obtain a proper energy expressed in Joules.
<h4 class=subsectionHead>Fourier series and transform</h4>
<p>
   The Fourier theorem for a periodic signal states that any periodic signal can be decomposed into pure signals which frequencies
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mi>n</mi>
         </mrow>
      </msub>
   </math>
   are multiples of the signal frequency
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mn>1</mn>
      <mo class=MathClass-bin>∕</mo>
      <mi>T</mi>
   </math>
   i.e
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mi>n</mi>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mi>n</mi>
      <msub>
         <mrow>
            <mi>f</mi>
         </mrow>
         <mrow>
            <mn>1</mn>
         </mrow>
      </msub>
   </math>
   .
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mi>s</mi>
            <mo class=MathClass-open>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <munderover accentunder=false accent=false>
               <mrow>
                  <mo>∑</mo>
               </mrow>
               <mrow>
                  <mi>n</mi>
                  <mo class=MathClass-rel>=</mo>
                  <mn>0</mn>
               </mrow>
               <mrow>
                  <mi>∞</mi>
               </mrow>
            </munderover>
            <msub>
               <mrow>
                  <mi>A</mi>
               </mrow>
               <mrow>
                  <mi>n</mi>
               </mrow>
            </msub>
            <mi class=qopname>cos</mi>
            <mo>⁡</mo>
            <mo class=MathClass-open>(</mo>
            <mn>2</mn>
            <mi>π</mi>
            <mi>n</mi>
            <msub>
               <mrow>
                  <mi>f</mi>
               </mrow>
               <mrow>
                  <mn>1</mn>
               </mrow>
            </msub>
            <mi>t</mi>
            <mo class=MathClass-bin>+</mo>
            <msub>
               <mrow>
                  <mi>ϕ</mi>
               </mrow>
               <mrow>
                  <mi>n</mi>
               </mrow>
            </msub>
            <mo class=MathClass-close>)</mo>
         </math>
      <td class=eq-no>(4)
</table>
<p>
   where
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>A</mi>
         </mrow>
         <mrow>
            <mi>n</mi>
         </mrow>
      </msub>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>ϕ</mi>
         </mrow>
         <mrow>
            <mi>n</mi>
         </mrow>
      </msub>
   </math>
   are the amplitude and phase of the
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msup>
         <mrow>
            <mi>n</mi>
         </mrow>
         <mrow>
            <mstyle class=text>
               <mtext>th</mtext>
            </mstyle>
         </mrow>
      </msup>
   </math>
   component. The first component (index
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>0</mn>
   </math>
   ) is the mean value, the second component is the fundamental (index 1), the third component (index 2) is the first harmonic, etc. This decomposition is unique and the frequency dependence of the amplitudes of the harmonics is called the amplitude spectrum. The Fourier transform is a generalisation of the Fourier series to non periodic signals. It states that any signal can be decomposed into a continuous sum of pure signals. It is then written as an integral sum (see <a href="${linkLesson0304}" target="_blank">Lesson 4</a> of the Acoustic Simulations and Auralization course) instead of a discrete sum
<table class=equation>
   <tr>
      <td style="font-size: 13px">
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mi>s</mi>
            <mo class=MathClass-open>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <msubsup>
               <mrow>
                  <mo>∫</mo>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
               <mrow>
                  <mi>∞</mi>
               </mrow>
            </msubsup>
            <mi>A</mi>
            <mo class=MathClass-open>(</mo>
            <mi>f</mi>
            <mo class=MathClass-close>)</mo>
            <mi class=qopname>cos</mi>
            <mo>⁡</mo>
            <mo class=MathClass-open>(</mo>
            <mn>2</mn>
            <mi>π</mi>
            <mi>f</mi>
            <mi>t</mi>
            <mo class=MathClass-bin>+</mo>
            <mi>ϕ</mi>
            <mo class=MathClass-open>(</mo>
            <mi>f</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-close>)</mo>
            <mstyle class=text>
               <mtext>d</mtext>
            </mstyle>
            <mi>f</mi>
         </math>
      <td class=eq-no>(5)
</table>
<p>
   where
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>A</mi>
      <mo class=MathClass-open>(</mo>
      <mi>f</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>ϕ</mi>
      <mo class=MathClass-open>(</mo>
      <mi>f</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   are the amplitude and phase spectrum. It must be pointed out that although this is a signal having a finite duration, it can nevertheless be decomposed into a sum of functions of infinite duration. This point should be kept in mind when doing physical interpretations of results when bursts are involved. A burst and its reflection on a wall interfere locally while their frequency components interfere across the whole space and time. In practice, measured signals have a finite duration
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>T</mi>
   </math>
   and they are sampled at discrete times. In that case the Fourier transform is replaced by the Discrete Fourier transform (DFT) which is nothing but the decomposition in Fourier series of a periodic signal with virtual period
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>T</mi>
   </math>
   .
<h4 class=subsectionHead>Complex notation</h4>
<p>For mathematical reasons the use of complex exponentials is prefered to cosine and sine functions. One of the reasons is that the derivative of an exponential is still an exponential while the derivative of a sine is a cosine and vice versa. In that case the Fourier transform writes
<table class=equation>
   <tr>
      <td>
         <math xmlns=http://www.w3.org/1998/Math/MathML display=block class=equation>
            <mi>s</mi>
            <mo class=MathClass-open>(</mo>
            <mi>t</mi>
            <mo class=MathClass-close>)</mo>
            <mo class=MathClass-rel>=</mo>
            <msubsup>
               <mrow>
                  <mo>∫</mo>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>−</mo>
                  <mi>∞</mi>
               </mrow>
               <mrow>
                  <mo class=MathClass-bin>+</mo>
                  <mi>∞</mi>
               </mrow>
            </msubsup>
            <mi>Â</mi>
            <mo class=MathClass-open>(</mo>
            <mi>f</mi>
            <mo class=MathClass-close>)</mo>
            <msup>
               <mrow>
                  <mi>e</mi>
               </mrow>
               <mrow>
                  <mstyle class=text>
                     <mtext>j</mtext>
                  </mstyle>
                  <mn>2</mn>
                  <mi>π</mi>
                  <mi>f</mi>
                  <mi>t</mi>
               </mrow>
            </msup>
            <mstyle class=text>
               <mtext>d</mtext>
            </mstyle>
            <mi>f</mi>
         </math>
      <td class=eq-no>(6)
</table>
<p>
   where
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>Â</mi>
      <mo class=MathClass-open>(</mo>
      <mi>f</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   is the complex amplitude at frequency
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>f</mi>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>j</mi>
      <mo class=MathClass-rel>=</mo>
      <msqrt>
         <mrow>
            <mo class=MathClass-bin>−</mo>
            <mn>1</mn>
         </mrow>
      </msqrt>
   </math>
   . From the complex amplitude
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>Â</mi>
      <mo class=MathClass-open>(</mo>
      <mi>f</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   , the effective amplitude
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>A</mi>
      <mo class=MathClass-open>(</mo>
      <mi>f</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   and phase
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>ϕ</mi>
      <mo class=MathClass-open>(</mo>
      <mi>f</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   can be deduced:
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>A</mi>
      <mo class=MathClass-open>(</mo>
      <mi>f</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <mn>2</mn>
      <mo class=MathClass-rel>|</mo>
      <mi>Â</mi>
      <mo class=MathClass-open>(</mo>
      <mi>f</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>|</mo>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>ϕ</mi>
      <mo class=MathClass-open>(</mo>
      <mi>f</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <mi>a</mi>
      <mi>r</mi>
      <mi>g</mi>
      <mo class=MathClass-open>(</mo>
      <mi>Â</mi>
      <mo class=MathClass-open>(</mo>
      <mi>f</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-close>)</mo>
   </math>
   .The factor of two for the amplitude is due to the fact that frequency component
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>f</mi>
   </math>
   is divided in two terms
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>Â</mi>
      <mo class=MathClass-open>(</mo>
      <mi>f</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>Â</mi>
      <mo class=MathClass-open>(</mo>
      <mo class=MathClass-bin>−</mo>
      <mi>f</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   . It is noticed that
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>Â</mi>
      <mo class=MathClass-open>(</mo>
      <mi>f</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>Â</mi>
      <mo class=MathClass-open>(</mo>
      <mo class=MathClass-bin>−</mo>
      <mi>f</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   are complex conjugate so that
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>s</mi>
      <mo class=MathClass-open>(</mo>
      <mi>t</mi>
      <mo class=MathClass-close>)</mo>
   </math>
   is still real. One of the advantages of the complex notation is that the time derivative corresponds, in frequency domain, to a simple multiplication by
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>j</mi>
      <mi>ω</mi>
   </math>
   with
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>ω</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>2</mn>
      <mi>π</mi>
      <mi>f</mi>
   </math>
   the pulsation (see <a href="${linkLesson0104}" target="_blank">Lesson 4</a>).
<h3 class=sectionHead>Sound as a wave</h3>
<h4 class=subsectionHead>Traveling wave</h4>
<p>Sounds are waves. A strict definition of a wave could be ”a propagating dynamic reversible disturbance”, that is a local modification of the state of a medium which propagates step by step with a given velocity, the speed of sound. The word reversible is here to exclude diffusion phenomena according to which the properties of the medium are durably modified. On the contrary after the passage of a wave, the medium recovers its initial properties. A complementary point is that waves propagate along given directions. The spreading of the wave being materialized by the wave front which can be either planar, spherical or any surface shape. The previous definition may be rather clear, but in physics courses the notion of wave is extended to all mathematical solutions of wave equations. So the wave, in a strict sense, is specified to be a ”traveling wave” which may seem to be a pleonasm since this property is still part of the definition.
<h4 class=subsectionHead>Pure wave</h4>
<p>A pure wave is a wave containing only one frequency. It is also called harmonic or monochromatic. The shape of the corresponding signals is sinusoidal. It must be pointed out that a pure wave has an indefinite extension both spatially -even not limited by the boundaries- as temporally -pure waves have neither beginning nor end. So, the pure wave as a pure signal has to be seen as a mathematical concept such as, for example, point source or perfect fluid.
<h4 class=subsectionHead>Standing wave</h4>
<p>A standing wave results from the superposition of two monochromatic waves (i.e. containing a single frequency) with the same frequency propagating in opposite directions. This definition is an extension of the wave concept. We emphasize that a standing wave corresponds to two traveling waves that interfere, and that a standing wave is necessarily monochromatic. It is sometimes heard in musical acoustics that bowed strings or wind instruments are the seat of standing waves. This is incorrect since in that case waves may be periodic, but rarely monochromatic. The condition of periodicity is not sufficient. So the sentence ”a pair of superimposed periodic waves traveling in opposite directions makes a standing wave” (Wave in Wikipedia) is not correct.
<h4 class=subsectionHead>Evanescent waves</h4>
<p>
   Evanescent waves are waves that do not propagate. This may sound like a contradiction since the property of a wave is to propagate. Thus, an evanescent wave is therefore in a sense a non-wave and results from the extension of the wave concept. This extension is related to the extension of numbers to complex numbers. Indeed, the amplitude of the acoustic pressure for a monochromatic progressive plane wave of frequency
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>f</mi>
   </math>
   propagating along the
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>x</mi>
   </math>
   axis can be written using complex numbers as
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>p</mi>
      <mo class=MathClass-open>(</mo>
      <mi>x</mi>
      <mo class=MathClass-punc>,</mo>
      <mi>ω</mi>
      <mo class=MathClass-close>)</mo>
      <mo class=MathClass-rel>=</mo>
      <mi>A</mi>
      <msup>
         <mrow>
            <mi>e</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>−</mo>
            <mstyle class=text>
               <mtext>j</mtext>
            </mstyle>
            <mi>k</mi>
            <mi>x</mi>
         </mrow>
      </msup>
   </math>
   where
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>ω</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>2</mn>
      <mi>π</mi>
      <mi>f</mi>
   </math>
   is the pulsation,
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>A</mi>
   </math>
   the amplitude for
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>x</mi>
      <mo class=MathClass-rel>=</mo>
      <mn>0</mn>
   </math>
   ,
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mstyle class=text>
         <mtext>j</mtext>
      </mstyle>
      <mo class=MathClass-rel>=</mo>
      <msqrt>
         <mrow>
            <mo class=MathClass-bin>−</mo>
            <mn>1</mn>
         </mrow>
      </msqrt>
   </math>
   and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>k</mi>
      <mo class=MathClass-rel>=</mo>
      <mi>ω</mi>
      <mo class=MathClass-bin>∕</mo>
      <mi>c</mi>
   </math>
   the wave number (see <a href="${linkLesson0104}" target="_blank">Lesson 4</a>). The wave number is a priori a real number, but it turns out to be imaginary in some situations. In this case, term
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msup>
         <mrow>
            <mi>e</mi>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>−</mo>
            <mstyle class=text>
               <mtext>j</mtext>
            </mstyle>
            <mi>k</mi>
            <mi>x</mi>
         </mrow>
      </msup>
   </math>
   , becomes a real exponential which means a rapid decrease of the amplitude without propagation, hence the expression ”evanescent wave”. Thus, mathematics leads us to consider solutions that are not really waves as (evanescent) waves. Some examples of evanescent waves can be found in dispersive media (see <a href="${linkLesson0125}" target="_blank">Lesson 25</a>).
<h3 class=sectionHead>Orders of magnitude</h3>
<p>A wave induces perturbations of the medium which cause local fluctuations of its properties. In the case of air, these fluctuations concern pressure, particle velocity, density, temperature and eventually other properties such as entropy. In the context of linear acoustics the fluctuations are small compared to the mean quantities. It is good to have an idea of the range of the quantities involved.
<h4 class=subsectionHead>Speed of sound</h4>
<p>
   Speed of sound in air
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>c</mi>
   </math>
   depends on the temperature (see <a href="${linkLesson0103}" target="_blank">Lesson 3</a>). It is about
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>3</mn>
      <mn>4</mn>
      <mn>0</mn>
   </math> m/s for a temperature around 20
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msup>
         <mrow></mrow>
         <mrow>
            <mn>o</mn>
         </mrow>
      </msup>
   </math>C.
<h4 class=subsectionHead>Pressure range</h4>
<p>
   In most applications, the acoustic waves propagate at atmospheric pressure which is around
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>1</mn>
      <msup>
         <mrow>
            <mn>0</mn>
         </mrow>
         <mrow>
            <mn>5</mn>
         </mrow>
      </msup>
   </math> Pa. Acoustic RMS pressure fluctuations at hearing threshold around
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>2</mn>
   </math> kHz are
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
      <mo class=MathClass-rel>=</mo>
      <mn>2</mn>
      <mo class=MathClass-bin>⋅</mo>
      <mn>1</mn>
      <msup>
         <mrow>
            <mn>0</mn>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>−</mo>
            <mn>5</mn>
         </mrow>
      </msup>
   </math> Pa. This value is used as the reference pressure for the calculation of pressure level and corresponds to
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>0</mn>
      <msub>
         <mrow>
            <mstyle class=text>
               <mtext> dB</mtext>
            </mstyle>
         </mrow>
         <mrow>
            <mi>S</mi>
            <mi>P</mi>
            <mi>L</mi>
         </mrow>
      </msub>
   </math>
   (see <a href="${linkLesson0122}" target="_blank">Lesson 22</a> for dB scale). Considering high pressure level, pain threshold is usually considered to be around
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>1</mn>
      <mn>2</mn>
      <mn>0</mn>
   </math> dB, that is
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>2</mn>
      <mn>0</mn>
   </math> Pa. It appears that the hearing dynamic is about
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>1</mn>
      <msup>
         <mrow>
            <mn>0</mn>
         </mrow>
         <mrow>
            <mn>6</mn>
         </mrow>
      </msup>
   </math>
   and that in any audible situation the acoustic pressure fluctuations are small compared to the mean pressure value. This justifies the linear acoustic approximation (see <a href="${linkLesson0102}" target="_blank">Lesson 2</a>).
<h4 class=subsectionHead>Particle displacement and volume velocity range</h4>
<p>
   In the case of traveling waves, far from sources, the particle velocity in the air is equal to
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>v</mi>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <mi>p</mi>
         </mrow>
         <mrow>
            <msub>
               <mrow>
                  <mi>ρ</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
            <mi>c</mi>
         </mrow>
      </mfrac>
   </math>
   , where
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
         <mrow>
            <mi>ρ</mi>
         </mrow>
         <mrow>
            <mn>0</mn>
         </mrow>
      </msub>
      <mo class=MathClass-rel>≈</mo>
      <mn>1</mn>
      <mo class=MathClass-punc>.</mo>
      <mn>2</mn>
    </math> kg.m<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
       <msup>
        <mrow></mrow>
         <mrow>
            <mo class=MathClass-bin>−</mo>
            <mn>3</mn>
         </mrow>
      </msup>
   </math>
   is the air density and
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>c</mi>
      <mo class=MathClass-rel>≈</mo>
      <mn>3</mn>
      <mn>4</mn>
      <mn>3</mn>
   </math> m/s  the speed of sound at atmospheric pressure and 20
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msup>
         <mrow></mrow>
         <mrow>
            <mn>o</mn>
         </mrow>
      </msup>
   </math>C.
   (see <a href="${linkLesson0103}" target="_blank">Lesson 3</a>). Thus, at the hearing threshold, the pressure being assumed to be
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msub>
        <mi>p</mi>
        <mn>0</mn>
      </msub>
      <mo>=</mo>
      <mn>2</mn>
      <mo>·</mo>
      <msup>
         <mrow>
            <mn>10</mn>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>−</mo>
            <mn>5</mn>
         </mrow>
      </msup>
   </math> Pa, the particle velocity is
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>v</mi>
      <mo class=MathClass-rel>≈</mo>
      <mfrac>
         <mrow>
            <msub>
               <mrow>
                  <mi>p</mi>
               </mrow>
               <mrow>
                  <mn>0</mn>
               </mrow>
            </msub>
         </mrow>
         <mrow>
            <mn>4</mn>
            <mn>0</mn>
            <mn>0</mn>
         </mrow>
      </mfrac>
      <mo class=MathClass-rel>=</mo>
      <mn>5</mn>
      <mo class=MathClass-bin>⋅</mo>
      <mn>1</mn>
      <msup>
         <mrow>
            <mn>0</mn>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>−</mo>
            <mn>8</mn>
         </mrow>
      </msup>
   </math>m.s<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msup>
         <mrow>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>−</mo>
            <mn>1</mn>
         </mrow>
      </msup>
      <mo class=MathClass-rel>=</mo>
      <mn>0</mn>
      <mo class=MathClass-punc>.</mo>
      <mn>0</mn>
      <mn>5</mn>
   </math> μ.m.s<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
    <msup><mrow></mrow>
         <mrow>
            <mo class=MathClass-bin>−</mo>
            <mn>1</mn>
         </mrow>
      </msup>
   </math>
   . This is only 10 times larger than the amplitude of the Brownian motion. In that case, the amplitude of the particle displacement at
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>1</mn>
      <mn>0</mn>
      <mn>0</mn>
      <mn>0</mn>
   </math> Hz is
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>η</mi>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <mi>v</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
            <mi>π</mi>
            <mi>f</mi>
         </mrow>
      </mfrac>
      <mo class=MathClass-rel>=</mo>
      <mn>8</mn>
      <mo class=MathClass-bin>⋅</mo>
      <mn>1</mn>
      <msup>
         <mrow>
            <mn>0</mn>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>−</mo>
            <mn>1</mn>
            <mn>2</mn>
         </mrow>
      </msup>
   </math> m, which is about 10 times less than the atomic radius. These numbers allow as to understand the amazing sensitivity of the human ear. At pain threshold particle velocity is
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>v</mi>
      <mo class=MathClass-rel>≈</mo>
      <mn>5</mn>
      <mo class=MathClass-bin>⋅</mo>
      <mn>1</mn>
      <msup>
         <mrow>
            <mn>0</mn>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>−</mo>
            <mn>2</mn>
         </mrow>
      </msup>
    </math> m.s<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msup>
        <mrow></mrow>
         <mrow>
            <mo class=MathClass-bin>−</mo>
            <mn>1</mn>
         </mrow>
      </msup>
   </math>
   which is still rather small compared to the speed of sound. The amplitude of the particle displacement at
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>1</mn>
      <mn>0</mn>
      <mn>0</mn>
      <mn>0</mn>
   </math> Hz is
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>η</mi>
      <mo class=MathClass-rel>=</mo>
      <mfrac>
         <mrow>
            <mi>v</mi>
         </mrow>
         <mrow>
            <mn>2</mn>
            <mi>π</mi>
            <mi>f</mi>
         </mrow>
      </mfrac>
      <mo class=MathClass-rel>=</mo>
      <mn>8</mn>
      <mo class=MathClass-bin>⋅</mo>
      <mn>1</mn>
      <msup>
         <mrow>
            <mn>0</mn>
         </mrow>
         <mrow>
            <mo class=MathClass-bin>−</mo>
            <mn>6</mn>
         </mrow>
      </msup>
   </math> m. This displacement is very small but when considering a loudspeaker at a low frequency, the membrane excursion can be of the order of
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>1</mn>
   </math> cm at low frequencies. A displacement of the order of
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>1</mn>
   </math> cm  at
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>1</mn>
      <mn>6</mn>
      <mstyle class=text>
      </mstyle>
   </math> Hz (
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mi>ω</mi>
      <mo class=MathClass-rel>≈</mo>
      <mn>1</mn>
      <mn>0</mn>
      <mn>0</mn>
   </math>
   ) corresponds to a particle velocity of the order of
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>1</mn></math> m/s, an acceleration of
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>1</mn>
      <mn>0</mn>
      <mn>0</mn>
    </math> m.s<math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <msup>
        <mrow></mrow>
         <mrow>
            <mo class=MathClass-bin>−</mo>
            <mn>2</mn>
         </mrow>
      </msup>
   </math>
   and a RMS pressure in front of the membrane of
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>4</mn>
      <mn>0</mn>
      <mn>0</mn>
      <mstyle class=text>
      </mstyle>        
   </math> Pa (
   <math xmlns=http://www.w3.org/1998/Math/MathML display=inline>
      <mn>1</mn>
      <mn>4</mn>
      <mn>6</mn>
      <mstyle class=text>
      </mstyle>
   </math> dB).
    <h3 class=likesectionHead>Glossary</h3>
<dl>
    <dt>DFT
    <dd>Discrete Fourier transform
  <dt>NDT
  <dd>Non-Destructive Testing
  <dt>RMS
  <dd>Root Mean Square
</dl>
   <h3 class=sectionHead>References</h3>
   <dl class=thebibliography>
     <dt>[1]
     <dd>Leo J. Beranek. Acoustics. The American Institute of Physics, New York, 1986.
     <dt>[2]
     <dd>A.D. Pierce. Acoustics: an Introduction to its Physical Principles and Applications. MCGraw-Hill Book Company, Inc., New York, 1981.
     <dt>[3]
     <dd>Lord Rayleigh. The Theory of Sound. Macmillan, 2nd edition, 1896.
     <dt>[4]
     <dd>Samuel Temkin. Elements of Acoustics. Wiley, New York, 1981.
   </dl>
`;

export default theory;
